import { LeftOutlined, PlusOutlined } from "@ant-design/icons"
import {
  App,
  Button,
  Flex,
  Pagination,
  Skeleton,
  Space,
  Typography
} from "antd"
import React, { Suspense, lazy, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { Link } from "react-router-dom"

import { CardComponent } from "../../../components/Card/CardComponent.tsx"
import { SearchBar } from "../../../components/Input/SearchBar.tsx"
import {
  paginationItemRender,
  paginationShowTotal
} from "../../../components/Table/pagination-footer.tsx"
import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { RoutesPaths } from "../../../constants/routes.ts"
import type { RootState } from "../../../store.ts"
import { documentTemplatesSetFilters } from "../redux/document-templates.slice.ts"
import {
  deleteTemplate,
  duplicateTemplate,
  fetchTemplateById,
  fetchTemplates,
  previewTemplate
} from "../redux/thunks/document-templates.thunks.ts"
import { DocumentTemplatesList } from "./components/DocumentTemplatesList.tsx"

const DocumentTemplatesPreview = lazy(
  () => import("./components/DocumentTemplatesPreview")
)

const { Title, Text } = Typography

function DocumentTemplatesPage({
  data,
  permissions,
  filters,
  fetching,
  getTemplates,
  removeTemplate,
  duplicateTemplate,
  fetchTemplateById,
  previewTemplate,
  setFilters
}: ConnectedProps<typeof connector>) {
  const [previewId, setPreviewId] = useState<string | null>()
  const { modal } = App.useApp()
  const { t } = useTranslation()

  const handleDeleteTemplate = (id: string) => {
    modal.confirm({
      centered: true,
      title: "Confirm deleting",
      content:
        "Are you sure you want to delete this template? This action can NOT be undone.",

      cancelText: "Cancel",
      okText: "Delete",
      okButtonProps: {
        danger: true,
        ghost: true
      },
      onOk: async () => {
        await removeTemplate(id)
        await getTemplates(filters)
      }
    })
  }

  const handleDuplicateTemplate = (id: string) => {
    modal.confirm({
      centered: true,
      title: "Confirm duplication",
      content:
        "Are you sure you want to duplicate this template? A new copy will be created.",

      cancelText: "Cancel",
      okText: "Duplicate",
      onOk: async () => {
        await duplicateTemplate(id)
        await getTemplates(filters)
      }
    })
  }

  const handlePreviewTemplate = async (id: string): Promise<string | void> => {
    try {
      const template = await fetchTemplateById(id).unwrap()
      return await previewTemplate(template).unwrap()
    } catch (e) {
      console.log(e)
    }
  }

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ page, page_size: pageSize })
  }

  // A narrower result set can leave the current page past the end of it.
  const handleSearchChange = (q: string | null) => {
    setFilters({ q, page: 1 })
  }

  useEffect(() => {
    getTemplates(filters)
    generateDocumentTitle("Settings Document Templates")
  }, [filters, getTemplates])

  return (
    <Flex vertical gap={"small"} className={"size-full"}>
      <Flex justify={"space-between"}>
        <Flex vertical>
          <Space>
            <Link to={RoutesPaths.settings.templates}>
              <Button icon={<LeftOutlined />} size={"small"} />
            </Link>
            <Title className={"m-0 text-start"} level={2}>
              {t("settingsTemplatesHub.cards.documents.title")}
            </Title>
          </Space>
          <Text type="secondary">
            Manage and use your proposal and contract templates
          </Text>
        </Flex>
        {permissions?.settings_proposal_template_create && (
          <Link to={RoutesPaths.documentTemplates.add}>
            <Button type="primary" icon={<PlusOutlined />}>
              {t("documentTemplates.addButton")}
            </Button>
          </Link>
        )}
      </Flex>

      {previewId && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className={"w-[50vw] h-[50vh] z-[999] absolute m-auto inset-0 "}
            />
          }
        >
          <DocumentTemplatesPreview
            previewId={previewId}
            onGetPreview={handlePreviewTemplate}
            onClose={() => setPreviewId(null)}
          />
        </Suspense>
      )}

      {/* One card holds the whole list, as on every other list page: search
          strip, divider, rows, footer. The rows keep their own layout — this
          tab shows templates, not a grid of fields. */}
      <CardComponent classNames={{ body: "p-0" }}>
        <Flex
          wrap="wrap"
          gap="small"
          className="py-4 md:p-4 border-b border-gray-200"
        >
          <div className="flex-1 max-md:basis-60 max-md:grow">
            <SearchBar
              placeholder={t("documentTemplates.searchPlaceholder")}
              defaultValue={filters.q ?? ""}
              onSearch={handleSearchChange}
            />
          </div>
        </Flex>

        {fetching ? (
          <Flex vertical gap="small" className="p-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton.Node active className={"w-full h-[68px]"} key={i} />
            ))}
          </Flex>
        ) : (
          <DocumentTemplatesList
            templates={data.results}
            hasSearch={!!filters.q}
            //
            copyAllowed={permissions?.settings_proposal_template_create}
            deleteAllowed={permissions?.settings_proposal_template_delete}
            editAllowed={permissions?.settings_proposal_template_edit}
            //
            onDelete={handleDeleteTemplate}
            onDuplicate={handleDuplicateTemplate}
            onPreview={setPreviewId}
          />
        )}

        {data.count > 0 && (
          <div className="py-3">
            <Pagination
              // `.custom-table` gives the table footer this same rule; the count
              // sits left, the controls right.
              className="px-4 [&_.ant-pagination-total-text]:mr-auto"
              current={filters.page}
              pageSize={filters.page_size}
              total={data.count}
              showLessItems
              showSizeChanger={false}
              showTotal={paginationShowTotal}
              itemRender={paginationItemRender(
                t,
                filters.page_size >= data.count
              )}
              onChange={handlePageChange}
            />
          </div>
        )}
      </CardComponent>
    </Flex>
  )
}

const mapStateToProps = (state: RootState) => ({
  permissions: state.auth.permissions?.codes?.settings?.document_templates,
  data: state?.documentTemplates?.data,
  filters: state?.documentTemplates?.filters,
  fetching: state?.documentTemplates?.fetching
})

const mapDispatchToProps = {
  getTemplates: fetchTemplates,
  removeTemplate: deleteTemplate,
  duplicateTemplate,
  previewTemplate,
  fetchTemplateById,
  setFilters: documentTemplatesSetFilters
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(DocumentTemplatesPage)
